'use client'

import ArrowLeftIcon from '@eversports/design-tokens/assets/icons/regular/arrow-left.svg?icon'
import ArrowRightIcon from '@eversports/design-tokens/assets/icons/regular/arrow-right.svg?icon'
import Button from '@eversports/klimt-primitives/Button'
import { useTranslations } from 'next-intl'
import { useLayoutEffect, useRef, useState } from 'react'

export enum Direction {
  FORWARD = 'FORWARD',
  BACK = 'BACK',
}

interface Props {
  title: string
  viewAllButtonServerComponent: React.ReactNode
  children: React.ReactNode
}

const Carousel = ({ title, viewAllButtonServerComponent, children }: Props) => {
  const containerRef = useRef<HTMLUListElement | null>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [childWidth, setChildWidth] = useState<number | undefined>(undefined)
  const [showArrows, setShowArrows] = useState(false)

  const translate = useTranslations('app')

  const handleScroll = (direction: Direction) => () => {
    if (!containerRef.current || !childWidth) return

    const currentScroll = containerRef.current.scrollLeft
    const availableScrollWidth = containerRef.current.scrollWidth

    switch (direction) {
      case Direction.FORWARD: {
        if (currentScroll + containerWidth === availableScrollWidth) {
          // If all the way to the end, scroll back to the beginning
          return (containerRef.current.scrollLeft = 0)
        } else if (currentScroll + childWidth < availableScrollWidth - containerWidth) {
          // If can't scroll a full card anymore, just scroll to the end of the container.
          return (containerRef.current.scrollLeft = currentScroll + childWidth)
        } else {
          // Otherwise, scroll by one card length.
          return (containerRef.current.scrollLeft = availableScrollWidth)
        }
      }
      case Direction.BACK: {
        if (currentScroll === 0) {
          // If all the way at the beginning, scroll all the way to the end
          return (containerRef.current.scrollLeft = availableScrollWidth - containerWidth)
        } else if (currentScroll - childWidth < 0) {
          // If all the way at the beginning, scroll all the way to the end
          return (containerRef.current.scrollLeft = 0)
        } else {
          // Otherwise, scroll by one card length.
          return (containerRef.current.scrollLeft = currentScroll - childWidth)
        }
      }
    }
  }

  // Need this for the re-render so we can get the updated ref dimensions
  useLayoutEffect(() => {
    if (containerRef.current) {
      const width = containerRef.current.clientWidth
      const scrollWidth = containerRef.current.scrollWidth
      const firstChild = containerRef.current.children?.[0] as HTMLLIElement | undefined
      setContainerWidth(width)
      setChildWidth(firstChild?.clientWidth)
      setShowArrows(scrollWidth > width)
    }
  }, [])

  return (
    <>
      <div className="mb-2 flex items-end justify-between gap-1">
        <h2 className="flex-1 text-xl font-medium">{title}</h2>
        {showArrows && (
          <>
            <Button
              size="sm"
              className="hidden lg:flex"
              onClick={handleScroll(Direction.BACK)}
              variant="tertiary"
              aria-label={translate('scroll-left-aria-label')}
            >
              <ArrowLeftIcon className="size-2 text-text-secondary" aria-hidden />
            </Button>
            <Button
              size="sm"
              className="hidden lg:flex"
              onClick={handleScroll(Direction.FORWARD)}
              variant="tertiary"
              aria-label={translate('scroll-right-aria-label')}
            >
              <ArrowRightIcon className="size-2 text-text-secondary" aria-hidden />
            </Button>
          </>
        )}
        {viewAllButtonServerComponent}
      </div>
      <ul ref={containerRef} className="flex snap-x gap-2 overflow-x-scroll scroll-smooth sm:mx-0">
        {children}
      </ul>
    </>
  )
}

export default Carousel
